import { useState } from 'react'

import { Box, Collapse, Stack, Typography } from '@mui/material'
import { Button, Card } from '@repo/ui'

import { PermissionGate } from 'components/access/PermissionGate'
import { ClearSampleDataButton } from 'components/feedback'
import { DisclosureTextButton } from 'components/input'
import { useIntroData, useMaterializeIntroData } from 'hooks/api'

/** `aria-controls` target — ties the disclosure trigger to the region it reveals. */
const OFFER_REGION_ID = 'sample-data-offer'

/**
 * The offer's heading, shared by both of its forms.
 *
 * One constant rather than the string written twice, because the accordion arm renders
 * it in its header row while the full arm renders it inside the body — two places, one
 * question, and a wording that drifts between them would make the same card read as two
 * different features depending on whether you had cleared before.
 *
 * Names VERA rather than "it" (was "Want to see it with something in it?"). On a Home
 * page that may be a brand's first ever screen, "it" has no antecedent — the reader has
 * not yet learned what this console is called — and "with something in it" describes the
 * mechanism rather than the offer. Saying "sample data" outright also sets up the badges
 * and banners those rows will carry once seeded, so the vocabulary is consistent from
 * the offer through to the thing it produces.
 */
const OFFER_HEADING = 'Want to see Vera with sample data in it?'

/**
 * Home's sample-data control — the whole lifecycle in ONE card (VRG-512).
 *
 * Was `TryWithSampleData`, which only ever offered the seed. Renamed rather than
 * joined by a sibling, because the two states are mutually exclusive and that is the
 * property most worth making structural.
 *
 * ── Why one component and not two ───────────────────────────────────────────────────
 * The obvious shape was a second `ClearSampleData` card rendered beside this one, each
 * with its own guard. That makes "exactly one shows" a coincidence maintained by hand:
 * two conditions that must stay complementary forever, in two files, with no mechanism
 * saying so. Refine one and you get both cards, or neither, and nothing fails.
 *
 * Here the states are the two arms of a single ternary on a single value, so both
 * cards CANNOT show and both cards CANNOT vanish. It is the same argument §42 makes
 * about the referrer prop that 35 of 37 call sites forgot: a convention nobody enforces
 * is not a mechanism.
 *
 * ── Why this exists at all ──────────────────────────────────────────────────────────
 * A brand's first login is every surface empty: Home, Products, Certificates, People
 * and Analytics all render zero states, which reads as a broken product rather than a
 * new one — and it leaves the tours with nothing to spotlight.
 *
 * ── Why it is a BUTTON and not automatic ────────────────────────────────────────────
 * The governing invariant is that an intro row is never written without an explicit
 * user action. Seeding on first login would be faster and is what the ticket originally
 * proposed; it was rejected because these rows land in a REAL, possibly paying tenant,
 * fenced only by `example_kind` (a demo company is fenced twice). Writing to someone's
 * live console because they logged in is not a decision the product gets to make for
 * them.
 *
 * The same invariant is why clearing is offered here rather than performed
 * automatically once real data arrives. Analytics already STOPS COUNTING the samples
 * the moment a real certificate exists (`IntroDataBanner`'s second state), but the rows
 * themselves stay until someone asks for them to go.
 *
 * ── Eligibility, and the reasoning that was wrong the first time ────────────────────
 * The seed arm originally rendered on `!hasIntroData` alone, arguing that checking for
 * real data client-side would create a second source of truth for a rule the endpoint
 * already enforces. The argument was right about the DUPLICATION and wrong about the
 * UX, and driving it in a browser showed the difference: on a tenant full of real
 * products the card appeared, the button POSTed, the server correctly answered
 * `{created: false, reason: 'tenant-has-real-data'}` — and nothing visible happened.
 *
 * Refusing an action and declining to OFFER it are different problems; the endpoint
 * only ever solved the first. The fix keeps one authority by having the SERVER publish
 * eligibility (`canSeed` on `GET /intro-data`) rather than the client re-deriving it.
 * That bug is easy to reintroduce here, because the reseed arm looks like it should be
 * keyed on `!hasIntroData` and must not be.
 *
 * ── NO SKELETON HERE, and that is a deliberate reading of §19 ───────────────────────
 * §19 wants a skeleton wherever async data will fill a region, sized to the content it
 * replaces. This card returns `null` until settled instead, because it is
 * SELF-SUPPRESSING: for an established brand (real products, samples never seeded) it
 * renders nothing, ever. A skeleton would reserve a card-sized block on every Home load
 * and then collapse it for the majority of tenants, which MANUFACTURES the layout shift
 * §19 exists to prevent rather than absorbing one. `RecentActivity` returns null for the
 * same reason and states it in the same terms.
 *
 * The flicker §19 actually cares about is handled by the `isReady` gate below: nothing
 * renders on an unsettled query, so neither arm can flash in and be replaced by the
 * other.
 *
 * ── THE OFFER IS QUIETER THE SECOND TIME (this revision) ────────────────────────────
 * The seed arm now has two forms, chosen by whether this tenant has cleared and not
 * re-seeded since (NOT "ever cleared" — the stamp is reset by the next seed; see
 * `IntroDataStatus.hasCleared`):
 *
 *   never cleared   the full card, exactly as before. A brand-new console is empty on
 *                   every surface, which reads as a broken product rather than a new
 *                   one, so the offer to fill it should be the loudest thing here.
 *   cleared before  a COLLAPSED accordion. Still present, still one click away, but it
 *                   does not re-open an argument the user already settled.
 *
 * The distinction is the point, and it is worth being explicit about why it needed new
 * plumbing rather than a client-side flag. `GET /intro-data`'s `{present: false,
 * canSeed: true}` is the IDENTICAL answer for both tenants — "no samples, eligible for
 * some" — and the clear is deliberately residue-free, so nothing about the data
 * distinguishes them afterwards. Hence `hasCleared`, recorded server-side inside the
 * clear's own transaction (`companies.intro_cleared_at`).
 *
 * Not `localStorage`: clearing is a TENANT-level act with tenant-level consequences.
 * Per-device state would show the same admin the collapsed card on their desktop and
 * the full offer on their laptop, and would show a second admin at the same brand an
 * offer they never had the chance to decline.
 *
 * ── Why `DisclosureTextButton` + `Collapse`, not MUI's `Accordion` ──────────────────
 * §28/§47: reuse the shared thing before reaching for base MUI. `DisclosureTextButton`
 * is this app's disclosure trigger, already carrying the parts that are easy to get
 * wrong — a real `<button>` with `aria-expanded`/`aria-controls`, a `focus-visible`
 * ring, the house down-means-expanded chevron convention, and a width that does not
 * change with its own state. MUI's `Accordion` brings its own summary/details chassis,
 * dividers, shadows and expand icon, none of which match a `Card`, and there is not one
 * usage of it anywhere in this app to be consistent with.
 *
 * The HEADING stays visible while collapsed rather than becoming the toggle itself. A
 * chevron-only row would be quieter still and would say nothing about what is behind
 * it; the question is short enough that showing it costs almost no prominence and
 * removes the need to expand just to find out.
 */
export const SampleDataCard = () => {
  const { hasIntroData, canSeed, hasCleared, isReady } = useIntroData()
  const { materialize, isMaterializing } = useMaterializeIntroData()

  // Starts CLOSED, per the product decision this arm exists for: the whole point of
  // the collapsed form is that a tenant who already cleared is not re-offered
  // prominently. Component state rather than anything persisted — re-collapsing on
  // navigation is the correct default for a re-offer, and a user who wants it open
  // again is one click away.
  //
  // Declared ABOVE the early returns because it is a hook: moving it below `if
  // (!isReady) return null` would change the hook order between the unsettled and
  // settled renders, which React rejects outright.
  const [offerExpanded, setOfferExpanded] = useState(false)

  // GATE ON THE QUERY HAVING SETTLED, never on the booleans. `hasIntroData` is
  // `boolean | undefined` and the third state is load-bearing (see `useIntroData`):
  // read `undefined` as `false` and a tenant WITH samples gets the "add sample data"
  // card for one frame, which is not merely a flicker but the wrong offer.
  if (!isReady) return null

  // `=== true` rather than a truthiness check, so the impossible-but-typed
  // settled-yet-undefined case falls to the seed arm's own guard instead of asserting
  // that samples exist.
  const hasSamples = hasIntroData === true

  // NOTHING AT ALL when there are no samples and none may be seeded — the tenant has
  // real products, so the offer would be one the server refuses. `canSeed` is the
  // server's own eligibility answer; do not substitute `!hasIntroData` for it.
  //
  // Note `canSeed` is independent of `hasSamples`, not its inverse: it reports "no real
  // products", which stays true while samples are present. That is exactly what makes
  // the reseed arm work after a clear.
  if (!hasSamples && !canSeed) return null

  /*
    Collapse the offer only when we KNOW this tenant cleared before. `=== true`, so the
    unsettled/absent case falls to the full card — the same tri-state discipline the
    guards above use, and the safer default of the two: showing a brand-new tenant the
    full offer is the intended behaviour, whereas collapsing it for them hides the thing
    the card exists to do.

    Nothing here re-checks `hasSamples` beyond the `!hasSamples` above: with samples
    present the card renders its clear arm regardless of this value.

    ⚠️ AN EARLIER NOTE HERE CONCLUDED FROM THAT that `hasCleared` "needs no reset when a
    tenant re-seeds". The premise is true and the conclusion did not follow. It only holds
    for samples that leave via the clear BUTTON; a sample set that goes away any other way
    lands on `!hasSamples && hasCleared === true` and gets the collapsed accordion over a
    decision this user never made. The server now NULLs the stamp on every seed
    (`materialize-intro-set.use-case.ts` step 13), so `hasCleared` means "cleared, and has
    not re-seeded since" and this expression reads literally.
  */
  const collapseOffer = !hasSamples && hasCleared === true

  /*
    Heading + copy + action, the card's substance. Extracted so the full arm and the
    accordion's revealed region render THE SAME NODE rather than two copies of it that
    can drift — the argument the docblock makes about one component instead of two,
    applied one level down (§36).
  */
  const body = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={4}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
    >
      <Stack spacing={2}>
        {/*
          The heading belongs to the FULL arm only. The accordion arm renders it up in
          its own header row, above the disclosure trigger and outside the collapsing
          region — so it stays readable while collapsed, and is never printed twice on
          one card.
        */}
        {!collapseOffer && (
          <Typography variant="h6">
            {hasSamples ? 'Your console is showing sample data' : OFFER_HEADING}
          </Typography>
        )}
        {/* Line-height 1.5 so the copy does not read dense (§15), and no em-dashes:
            short sentences instead of dash-joined clauses. */}
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {hasSamples
            ? 'A few sample products, certificates and scans were added so you could ' +
              'look around. They are labelled as samples and are never billed. Clear ' +
              'them whenever you are ready.'
            : 'We can add a few sample products, certificates and scans so you can ' +
              'look around before adding your own. They are clearly labelled, never ' +
              'billed, and you can clear them in one click.'}
        </Typography>
      </Stack>
      {hasSamples ? (
        // The shared control (§36) — it owns the confirm, the loading state and the
        // leave-open-on-failure behaviour, so this surface and the Analytics banner
        // cannot drift on any of the three. Only the consequence copy is ours,
        // because only we know what the reader is looking at.
        <ClearSampleDataButton
          /*
            What the reader will actually observe, stated for THIS page (§43). It
            deliberately does not promise the console goes back to empty: samples can
            still be present after real products exist (`canSeed` false, `hasIntroData`
            true), so "your console will be empty again" would be false for exactly the
            tenant most likely to be clearing.
          */
          consequence={
            'Every sample record will be deleted. That is the sample products and ' +
            'their certificates, the scan history, and the sample owners, tags, ' +
            'addresses, sales and orders. Your own data is not affected. Lists and ' +
            'figures across the console will stop showing the samples.'
          }
          sx={{ flexShrink: 0 }}
        />
      ) : (
        /*
          Seeding is gated on the same permission as clearing (VRG-512): `POST
          /intro-data` now carries `@Permissions(SampleDataManage)`, so without it
          this button would POST and 403 with nothing visible happening — the exact
          failure the `canSeed` work above was written to fix, arriving by a
          different route.

          Disabled, not hidden (§27/§46). The card's own copy is an offer, and an
          offer whose button silently vanishes for some roles reads as a rendering
          bug. Disabled with a tooltip naming the permission says who to ask.
        */
        <PermissionGate
          permission="sample_data_manage"
          tooltip="You need the “Manage sample data” permission to add samples. Ask an admin to grant it, or to add them for you."
        >
          <Button
            variant="primary"
            onClick={() => materialize('home-cta')}
            loading={isMaterializing}
            sx={{ flexShrink: 0 }}
          >
            {/* Default size (40px). `size="small"` is never correct on the shared
                Button (§60). */}
            Add sample data
          </Button>
        </PermissionGate>
      )}
    </Stack>
  )

  // The full card: samples present, or an offer this tenant has never declined.
  if (!collapseOffer) return <Card>{body}</Card>

  return (
    <Card>
      {/*
        The header row, always visible. The heading answers "what is this card", the
        trigger answers "how do I get at it" — and because the trigger's label names the
        ACTION rather than the content, the two do not say the same thing twice.
      */}
      <Stack spacing={3}>
        <Typography variant="h6">{OFFER_HEADING}</Typography>
        <DisclosureTextButton
          expanded={offerExpanded}
          onToggle={() => setOfferExpanded(open => !open)}
          /*
            The PAIR, not a ternary. `DisclosureTextButton` reserves the width of the
            wider label so the control does not resize as it is pressed — resolving the
            ternary at the call site is exactly what defeats that (its own docblock
            carries the measurements).
          */
          label={{ expanded: 'Hide', collapsed: 'Show me' }}
          controls={OFFER_REGION_ID}
        />
      </Stack>

      {/*
        OUTSIDE the `Stack spacing={3}` above, deliberately, and this is the one
        construction detail that is easy to get wrong.

        A `Collapse` placed INSIDE a spaced Stack still receives the Stack's gap while
        collapsed — its height animates to 0 but the sibling margin does not — so the
        card would carry 12px of unexplained dead space below the trigger in its resting
        state, which is the state it spends most of its life in. Keeping it out here and
        letting the revealed content own its own top padding means the collapsed card is
        exactly its header row, and the 12px arrives as part of the same height
        animation rather than sitting there beforehand.

        `Card`'s own children wrapper is `<Stack spacing={0}>`, which emits no gap at all
        (its docblock records that MUI skips the reset for a falsy spacing), so this
        `Collapse` is a safe direct child of it.
      */}
      <Collapse in={offerExpanded} id={OFFER_REGION_ID}>
        <Box sx={{ pt: 3 }}>{body}</Box>
      </Collapse>
    </Card>
  )
}
